import {
  OrphanStatus,
  PairImportPreview,
  RollMode,
  RollPlanStatus,
  RollWarningState,
} from '../services/pairTradingApi';

/**
 * Presentation of the futures-roll plan. No date arithmetic lives here.
 *
 * Every date in `RollPlanStatus` is already resolved by the BE against the
 * holiday calendar that is also published to the engine, so this module only
 * FORMATS and LABELS. Re-deriving the third-Thursday rule in the browser is how
 * you get a UI that disagrees with the bot on exactly the months a holiday moves
 * the expiry — the rule has three homes already ([[project-pair-futures-autoroll]]).
 */

/** ISO `YYYY-MM-DD` → `dd/MM/yyyy`, the form a VN operator reads. */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function rollModeLabel(mode: RollMode): string {
  switch (mode) {
    case 'AUTO':
      return 'Automatic';
    case 'MANUAL':
      return 'On my date';
    case 'OFF':
      return "Don't roll";
    default:
      return mode;
  }
}

export function rollModeHelp(mode: RollMode): string {
  switch (mode) {
    case 'AUTO':
      return 'Rolls on the last trading day before expiry, at 14:00.';
    case 'MANUAL':
      return 'Rolls on the date you pick, any trading day up to expiry.';
    case 'OFF':
      return 'No roll. At expiry the exchange cash-settles the futures leg and the stock leg is left on its own.';
    default:
      return '';
  }
}

/** "2 trading days left" / "expires today" / "expired". */
export function countdownLabel(status: RollPlanStatus): string {
  const sessions = status.warning.sessionsToExpiry;
  if (sessions == null) return 'expiry unknown';
  if (status.plan.expiry && status.today > status.plan.expiry) return 'expired';
  if (sessions === 0) return 'expires today';
  if (sessions === 1) return '1 trading day left';
  return `${sessions} trading days left`;
}

/** antd Alert type for a warning severity. */
export function warningTone(severity: RollWarningState['severity']): 'info' | 'warning' | 'error' {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'info';
}

/** One line describing what will happen, for the panel header. */
export function planSummary(status: RollPlanStatus): string {
  const { plan } = status;
  if (plan.source === 'unknown-contract') {
    return plan.note ?? 'the futures contract cannot be read';
  }
  if (plan.mode === 'OFF') {
    return `No roll — ${plan.contract} is cash-settled on ${formatIsoDate(plan.expiry)}`;
  }
  if (!plan.rollDate) return 'no roll scheduled';
  const target = plan.nextContract ?? 'the next contract';
  return `Rolls to ${target} on ${formatIsoDate(plan.rollDate)} at ${plan.rollTime}`;
}

/**
 * Should the roll banner be on screen?
 *
 * An acknowledged warning is hidden — the operator said they know, and saying it
 * again every minute is what "don't spam me" rules out. The two states that
 * override an acknowledgement are the ones where something already went wrong:
 * an orphaned leg, and a roll date that came and went with the contract still
 * un-rolled.
 */
export function shouldShowBanner(status: RollPlanStatus | null): boolean {
  if (!status) return false;
  if (status.orphan.orphaned) return true;
  if (status.missed) return true;
  return status.warning.warn;
}

export type BannerKind = 'orphan' | 'missed' | 'expiry';

export function bannerKind(status: RollPlanStatus): BannerKind {
  if (status.orphan.orphaned) return 'orphan';
  if (status.missed) return 'missed';
  return 'expiry';
}

/** Whether "Got it" can silence what is on screen. Only the plain expiry notice. */
export function isDismissible(status: RollPlanStatus): boolean {
  return bannerKind(status) === 'expiry';
}

/** Headline for the missed-roll case, which is recoverable but time-boxed. */
export function missedRollMessage(status: RollPlanStatus): string {
  const { plan } = status;
  return (
    `The roll for ${plan.contract} was due on ${formatIsoDate(plan.rollDate)} and has not ` +
    `happened — the bot was not running through that window. The contract still trades until ` +
    `${formatIsoDate(plan.expiry)}: start the pair (it rolls on the next tick) or roll the leg ` +
    'by hand before then.'
  );
}

export function orphanHeadline(orphan: OrphanStatus): string {
  return (
    `${orphan.contract ?? 'The futures contract'} expired on ${formatIsoDate(orphan.expiry)} ` +
    'and was cash-settled by the exchange — this pair is half open.'
  );
}

/** Weights an operator edited, normalised for display as percentages. */
export function normalisedWeights(
  legs: { symbol: string; weight: number }[],
): { symbol: string; percent: number }[] {
  const total = legs.reduce((sum, leg) => sum + (leg.weight > 0 ? leg.weight : 0), 0);
  return legs.map((leg) => ({
    symbol: leg.symbol,
    percent: total > 0 ? ((leg.weight > 0 ? leg.weight : 0) / total) * 100 : 0,
  }));
}

/** Is the import worth submitting? Mirrors the BE guards, to fail before the call. */
export function pairImportBlockers(
  preview: PairImportPreview | null,
  legs: { symbol: string; weight: number }[],
  confirmText: string,
): string[] {
  const blockers: string[] = [];
  if (!preview) return ['loading the broker snapshot…'];
  if (preview.isRunning) blockers.push('stop the pair first — a running worker would overwrite the import');
  if (legs.length === 0) blockers.push('add at least one stock symbol');
  if (legs.reduce((sum, leg) => sum + leg.weight, 0) <= 0) blockers.push('weights must add up to more than zero');
  if (legs.some((leg) => !/^[A-Z0-9]{3,12}$/.test(leg.symbol))) blockers.push('every symbol must be a bare ticker, e.g. HPG');
  if (confirmText.trim() !== preview.confirmationPhrase) {
    blockers.push(`type ${preview.confirmationPhrase} to confirm`);
  }
  return blockers;
}
